import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

const API = '';

function decodeEntities(text) {
  if (!text) return '';
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

function useJobPoller(jobId) {
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  useEffect(() => {
    if (!jobId) return;
    setStatus('processing'); setResult(null); setError(null);
    intervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/job/${jobId}`);
        const data = await res.json();
        setStatus(data.status);
        if (data.status === 'done') { setResult(data.result); clearInterval(intervalRef.current); }
        else if (data.status === 'error') { setError(data.error); clearInterval(intervalRef.current); }
      } catch { clearInterval(intervalRef.current); setError('Polling failed'); }
    }, 2000);
    return () => clearInterval(intervalRef.current);
  }, [jobId]);
  return { result, status, error };
}

// Priority label config
const PRIORITY_LABELS = [
  { id: 'ACTION', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', text: '#fca5a5' },
  { id: 'AWAITING', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', text: '#93c5fd' },
  { id: 'FOLLOW-UP', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', text: '#c4b5fd' },
  { id: 'PAYMENT', color: '#22c55e', bg: 'rgba(34,197,94,0.1)', text: '#86efac' },
  { id: 'LEGAL', color: '#eab308', bg: 'rgba(234,179,8,0.1)', text: '#fde047' },
  { id: 'SYSTEM', color: '#64748b', bg: 'rgba(100,116,139,0.1)', text: '#94a3b8' },
  { id: 'GENERAL', color: '#475569', bg: 'rgba(100,116,139,0.05)', text: '#64748b' },
];

function App() {
  const [authStatus, setAuthStatus] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [recommendedTone, setRecommendedTone] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [labelCounts, setLabelCounts] = useState({});
  const [filterPriority, setFilterPriority] = useState(null);
  const [filterProject, setFilterProject] = useState(null);
  const [projectCounts, setProjectCounts] = useState({});
  const [nextTokenPersonal, setNextTokenPersonal] = useState(null);
  const [nextToken3am, setNextToken3am] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/auth/status`).then(r => r.json()).then(setAuthStatus)
      .catch(() => setAuthStatus({ personal: { connected: false }, '3am': { connected: false } }));
  }, []);

  const fetchEmails = useCallback(async (append = false) => {
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    try {
      const params = new URLSearchParams({ account: activeTab, max_results: '100' });
      if (searchQuery) params.set('query', searchQuery);
      if (filterPriority) params.set('label_priority', filterPriority);
      if (filterProject) params.set('label_project', filterProject);
      if (append) {
        if (nextTokenPersonal) params.set('page_token_personal', nextTokenPersonal);
        if (nextToken3am) params.set('page_token_3am', nextToken3am);
      }
      const res = await fetch(`${API}/api/emails?${params}`);
      const data = await res.json();
      const newEmails = data.emails || [];
      if (append) {
        setEmails(prev => {
          const existingIds = new Set(prev.map(e => e.id));
          const unique = newEmails.filter(e => !existingIds.has(e.id));
          return [...prev, ...unique];
        });
      } else {
        setEmails(newEmails);
      }
      if (data.label_counts && !append) setLabelCounts(data.label_counts);
      setNextTokenPersonal(data.next_page_token_personal || null);
      setNextToken3am(data.next_page_token_3am || null);
      setHasMore(!!(data.next_page_token_personal || data.next_page_token_3am));
      // Compute project counts from all emails
      if (!append) {
        const pc = {};
        newEmails.forEach(e => {
          const proj = e.ai_project;
          if (proj && proj !== 'none') pc[proj] = (pc[proj] || 0) + 1;
        });
        setProjectCounts(pc);
      }
    } catch (err) { console.error(err); }
    if (append) { setLoadingMore(false); } else { setLoading(false); }
  }, [activeTab, searchQuery, filterPriority, filterProject, nextTokenPersonal, nextToken3am]);

  // Trigger fetch on auth connect (initial load only)
  const hasConnected = useRef(false);
  useEffect(() => {
    const connected = authStatus && (authStatus.personal?.connected || authStatus['3am']?.connected);
    if (connected && !hasConnected.current) {
      hasConnected.current = true;
      fetchEmails();
    }
  }, [authStatus, fetchEmails]);

  // Re-fetch when filters change (reset pagination)
  useEffect(() => {
    if (hasConnected.current) {
      setNextTokenPersonal(null);
      setNextToken3am(null);
      setHasMore(true);
      fetchEmails(false);
    }
  }, [activeTab, filterPriority, filterProject]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) fetchEmails(true);
  }, [loadingMore, hasMore, fetchEmails]);

  // Infinite scroll handler
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
        loadMore();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [loadMore]);

  const [threadLoading, setThreadLoading] = useState(false);

  const openThread = async (email) => {
    setSelectedEmail(email);
    setThreadMessages([]);
    setThreadLoading(true);
    fetch(`${API}/api/interactions/log`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'opened', email_id: email.id, thread_id: email.thread_id, from_addr: email.from, subject: email.subject, account: email.account }),
    }).catch(() => {});
    try {
      const res = await fetch(`${API}/api/thread/${email.account}/${email.thread_id}`);
      const data = await res.json();
      setThreadMessages(data.messages || []);
      setRecommendedTone(data.recommended_tone || null);
    } catch (err) { console.error('Thread fetch error:', err); }
    setThreadLoading(false);
  };

  const isConnected = authStatus && (authStatus.personal?.connected || authStatus['3am']?.connected);

  // Auth screen
  if (!isConnected) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1 className="auth-title"><span className="blue">Gmail</span> Intelligence</h1>
          <p className="auth-sub">3AM Digital</p>
          <div className="auth-btns">
            <a href="/auth/login/personal" className="auth-btn personal">Connect Personal Gmail</a>
            <a href="/auth/login/3am" className="auth-btn work">Connect 3AM Workspace</a>
          </div>
        </div>
      </div>
    );
  }

  // Main 3-column layout (settings is an overlay rendered below)
  return (
    <>
    {showSettings && (
      <div className="settings-fullpage">
        <div className="settings-inner">
          <div className="settings-header">
            <h2>Settings</h2>
            <button className="ghost-btn" onClick={() => setShowSettings(false)}>Back to inbox</button>
          </div>
          <SettingsPanel />
        </div>
      </div>
    )}
    
    <div className="layout">
      {/* LEFT NAV */}
      <nav className="left-nav">
        <div className="nav-logo">
          <span className="blue">G</span>mail Intelligence
        </div>

        <div className="nav-section">
          <div className="nav-section-title">ACCOUNTS</div>
          {['all', 'personal', '3am'].map(tab => (
            <button key={tab} className={`nav-item ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}>
              {tab === 'all' ? 'All Inboxes' : tab === 'personal' ? 'Personal Gmail' : '3AM Digital'}
              {tab !== 'all' && authStatus?.[tab]?.connected && <span className="nav-dot connected" />}
            </button>
          ))}
        </div>

        <div className="nav-section">
          <div className="nav-section-title">PRIORITY</div>
          {PRIORITY_LABELS.map(l => (
            <button key={l.id}
              className={`nav-item label-nav ${filterPriority === l.id ? 'active' : ''}`}
              onClick={() => setFilterPriority(filterPriority === l.id ? null : l.id)}>
              <span className="nav-label-dot" style={{ background: l.color }} />
              {l.id}
              {labelCounts[l.id] && <span className="nav-count">{labelCounts[l.id].total}</span>}
            </button>
          ))}
          {filterPriority && (
            <button className="nav-item dim" onClick={() => setFilterPriority(null)}>Clear filter</button>
          )}
        </div>

        <div className="nav-section">
          <div className="nav-section-title">PROJECTS</div>
          {['NEOM', 'Emirates', 'Cisco', 'DET', 'MOHAP', 'Government-UAE', 'Shipments', 'Finance', 'Legal', 'MASAR', 'Villa-Evita', '3AM-Internal'].map(name => {
            const count = projectCounts[name] || projectCounts[name.toUpperCase()] || 0;
            return (
              <button key={name}
                className={`nav-item label-nav ${filterProject === name ? 'active' : ''}`}
                onClick={() => setFilterProject(filterProject === name ? null : name)}>
                <span className="nav-label-dot" style={{ background: '#8b5cf6' }} />
                {name}
                {count > 0 && <span className="nav-count">{count}</span>}
              </button>
            );
          })}
          {filterProject && (
            <button className="nav-item dim" onClick={() => setFilterProject(null)}>Clear filter</button>
          )}
        </div>

        <div className="nav-section nav-bottom">
          <button className="nav-item" onClick={() => setShowSettings(true)}>Settings</button>
        </div>
      </nav>

      {/* CENTER — EMAIL CARD GRID */}
      <main className="center-panel" ref={listRef}>
        <div className="search-row">
          <input type="text" className="search-input" placeholder="Search emails..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchEmails()} />
          <button className="search-btn" onClick={fetchEmails}>Search</button>
        </div>

        <div className="card-grid">
          {loading && <div className="loading-msg">Loading...</div>}
          {!loading && emails.length === 0 && <div className="empty-msg">No emails to show</div>}
          {emails.map(email => {
            const pl = PRIORITY_LABELS.find(l => l.id === email.ai_priority) || PRIORITY_LABELS[6];
            return (
              <div key={email.id}
                className={`icard ${selectedEmail?.id === email.id ? 'sel' : ''} ${email.is_unread ? 'unread' : ''}`}
                onClick={() => openThread(email)}>
                <div className="icard-top">
                  {email.ai_priority && (
                    <span className="ptag" style={{ background: pl.bg, color: pl.text, borderColor: pl.color + '33' }}>
                      {email.ai_priority}
                    </span>
                  )}
                  <span className="acct-badge" style={{ background: email.account === 'personal' ? '#3b82f6' : '#f97316' }}>
                    {email.account_label}
                  </span>
                  <span className="icard-date">{new Date(email.date_ts * 1000).toLocaleDateString()}</span>
                </div>
                <div className="icard-sender">{email.from}</div>
                <div className="icard-subject">{email.subject}</div>
                {email.ai_reason && (
                  <div className="intent-box">{email.ai_reason}</div>
                )}
                <div className="icard-snippet">{decodeEntities(email.snippet)}</div>
              </div>
            );
          })}
          {loadingMore && <div className="loading-msg">Loading more emails...</div>}
          {!hasMore && emails.length > 0 && <div className="empty-msg" style={{padding:'12px',fontSize:'11px'}}>All emails loaded ({emails.length})</div>}
        </div>
      </main>

      {/* RIGHT — AI / DRAFT PANEL (always visible) */}
      <aside className="right-panel">
        <AIPanel
          email={selectedEmail}
          recommendedTone={recommendedTone}
        />
      </aside>

      {/* EMAIL POPUP — opens over the card grid */}
      {selectedEmail && (
        <div className="email-popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setSelectedEmail(null); setThreadMessages([]); } }}>
          <div className="email-popup">
            <div className="popup-header">
              <h3 className="popup-title">{selectedEmail.subject}</h3>
              <span className="acct-badge sm" style={{ background: selectedEmail.account === 'personal' ? '#3b82f6' : '#f97316' }}>
                {selectedEmail.account_label}
              </span>
              <div className="popup-actions">
                <button className="act-btn" onClick={async () => {
                  await fetch(`${API}/api/emails/archive`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account: selectedEmail.account, message_ids: threadMessages.map(m => m.id) }) });
                  setSelectedEmail(null); setThreadMessages([]); fetchEmails();
                }}>Archive</button>
                <button className="act-btn" onClick={async () => {
                  await fetch(`${API}/api/emails/star`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account: selectedEmail.account, message_ids: threadMessages.map(m => m.id) }) });
                }}>Star</button>
              </div>
              <button className="popup-close" onClick={() => { setSelectedEmail(null); setThreadMessages([]); }}>x</button>
            </div>
            <div className="popup-body">
              {threadLoading && <div className="ai-loading"><div className="spinner" /> Loading thread...</div>}
              {!threadLoading && threadMessages.map((msg, i) => (
                <div key={i} className="thread-msg">
                  <div className="msg-top">
                    <strong>{msg.from}</strong>
                    <span className="msg-date">{new Date(msg.date_ts * 1000).toLocaleString()}</span>
                  </div>
                  <div className="msg-body" dangerouslySetInnerHTML={{ __html: msg.body }} />
                  {msg.attachments?.length > 0 && (
                    <div className="msg-atts">
                      {msg.attachments.map((att, j) => (
                        <span key={j} className="att-chip">att: {att.filename} ({(att.size / 1024).toFixed(0)}KB)</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CHAT BAR */}
      <ChatBar />
    </div>
    </>
  );
}

// ─── Thread + AI Panel ────────────────────────────────────────────────────────

const VOICE_NAMES = {
  'dimitri_client': 'Dimitri — client formal',
  'evita_client': 'Evita — client warm',
  'haris_operational': 'Haris — operational',
  'haris_personal': 'Haris — personal',
};

function AIPanel({ email, recommendedTone }) {
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [analysisMode, setAnalysisMode] = useState('summary');
  const [draftJobId, setDraftJobId] = useState(null);
  const [draftInstruction, setDraftInstruction] = useState('');
  const [draftProfileId, setDraftProfileId] = useState(recommendedTone?.tone || 'haris_operational');
  const [draftLanguage, setDraftLanguage] = useState('en');
  const analysis = useJobPoller(analysisJobId);
  const draft = useJobPoller(draftJobId);

  // Reset when email changes
  useEffect(() => {
    setAnalysisJobId(null);
    setDraftJobId(null);
    setDraftInstruction('');
    if (recommendedTone?.tone) setDraftProfileId(recommendedTone.tone);
  }, [email?.id, recommendedTone]);

  if (!email) {
    return (
      <div className="ai-panel-empty">
        <div className="ai-panel-title">AI Assistant</div>
        <p className="right-empty-text">Select an email to analyse or draft a reply</p>
      </div>
    );
  }

  const runAnalysis = async (mode) => {
    setAnalysisMode(mode);
    const res = await fetch(`${API}/api/analyse`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account: email.account, thread_id: email.thread_id, mode }),
    });
    setAnalysisJobId((await res.json()).job_id);
  };

  const runDraft = async () => {
    if (!draftInstruction.trim()) return;
    const res = await fetch(`${API}/api/draft`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account: email.account, thread_id: email.thread_id, instruction: draftInstruction, profile_id: draftProfileId, relationship: 'existing_client', language: draftLanguage }),
    });
    setDraftJobId((await res.json()).job_id);
  };

  return (
    <div className="ai-panel">
      <div className="ai-panel-title">AI Assistant</div>
      <div className="ai-panel-subject">{email.subject}</div>

      {/* Analysis buttons */}
      <div className="ai-section-title">ANALYSE</div>
      <div className="ai-bar">
        {['summary', 'actions', 'timeline', 'data', 'sentiment'].map(m => (
          <button key={m} className={`ai-btn ${analysisMode === m && analysis.status ? 'active' : ''}`}
            onClick={() => runAnalysis(m)}>{m}</button>
        ))}
      </div>

      {analysis.status && (
        <div className="ai-result">
          {analysis.status === 'processing' && <div className="ai-loading"><div className="spinner" /> Analyzing...</div>}
          {analysis.status === 'done' && analysis.result && (
            <div className="ai-text">{analysis.result.structured ? JSON.stringify(analysis.result.structured, null, 2) : analysis.result.text}</div>
          )}
          {analysis.status === 'error' && <div className="ai-error">{analysis.error}</div>}
        </div>
      )}

      {/* Draft section */}
      <div className="ai-section-title">DRAFT REPLY</div>
      <div className="draft-box">
        <div className="draft-controls">
          <label className="draft-label">
            <span className="draft-label-text">From</span>
            <select value={email.account} disabled>
              <option value="personal">hpadouvas@gmail.com</option>
              <option value="3am">hpadouvas@3am.digital</option>
            </select>
          </label>
          <label className="draft-label">
            <span className="draft-label-text">Voice</span>
            <select value={draftProfileId} onChange={e => setDraftProfileId(e.target.value)}>
              <option value="dimitri_client">Dimitri — client formal</option>
              <option value="evita_client">Evita — client warm</option>
              <option value="haris_operational">Haris — operational</option>
              <option value="haris_personal">Haris — personal</option>
            </select>
          </label>
          <label className="draft-label">
            <span className="draft-label-text">Language</span>
            <select value={draftLanguage} onChange={e => setDraftLanguage(e.target.value)}>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
            </select>
          </label>
        </div>
        {recommendedTone && (
          <div className="tone-auto-hint">Auto: {VOICE_NAMES[recommendedTone.tone] || recommendedTone.tone} — {recommendedTone.reason}</div>
        )}
        <textarea className="draft-input" rows={3} placeholder="What should the reply say?"
          value={draftInstruction} onChange={e => setDraftInstruction(e.target.value)} />
        <div className="draft-actions">
          <button className="search-btn" onClick={runDraft}>Generate Draft</button>
          <button className="ghost-btn sm" onClick={async () => {
            const res = await fetch(`${API}/api/create-storyboard`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ account: email.account, thread_id: email.thread_id, subject: email.subject }),
            });
            const data = await res.json();
            if (data.studio_url) window.open(data.studio_url, '_blank');
          }}>Storyboard</button>
        </div>
        {draft.status === 'processing' && <div className="ai-loading"><div className="spinner" /> Drafting...</div>}
        {draft.status === 'done' && draft.result && (
          <div className={`draft-output ${draft.result.language === 'ar' ? 'rtl' : ''}`}>
            {draft.result.draft}
            <button className="ghost-btn sm" onClick={() => navigator.clipboard.writeText(draft.result.draft)}>Copy</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Settings Panel ───────────────────────────────────────────────────────────

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [newVal, setNewVal] = useState('');
  const [aiStatus, setAiStatus] = useState({});

  const runAiAction = async (name, endpoint) => {
    setAiStatus(prev => ({ ...prev, [name]: 'loading' }));
    try {
      const res = await fetch(`${API}${endpoint}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setAiStatus(prev => ({ ...prev, [name]: 'done' }));
        setTimeout(() => setAiStatus(prev => ({ ...prev, [name]: null })), 3000);
      } else {
        setAiStatus(prev => ({ ...prev, [name]: 'error' }));
        setTimeout(() => setAiStatus(prev => ({ ...prev, [name]: null })), 4000);
      }
    } catch (err) {
      console.error(err);
      setAiStatus(prev => ({ ...prev, [name]: 'error' }));
      setTimeout(() => setAiStatus(prev => ({ ...prev, [name]: null })), 4000);
    }
  };

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(setSettings);
  }, []);

  if (!settings) return <p>Loading...</p>;

  const addItem = async (path, value) => {
    if (!value.trim()) return;
    const updated = JSON.parse(JSON.stringify(settings));
    const keys = path.split('.');
    let t = updated;
    for (let i = 0; i < keys.length - 1; i++) t = t[keys[i]];
    if (!t[keys[keys.length - 1]].includes(value.trim())) t[keys[keys.length - 1]].push(value.trim());
    await fetch(`${API}/api/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated) });
    setSettings(updated);
    setNewVal('');
  };

  const removeItem = async (path, index) => {
    const updated = JSON.parse(JSON.stringify(settings));
    const keys = path.split('.');
    let t = updated;
    for (let i = 0; i < keys.length - 1; i++) t = t[keys[i]];
    t[keys[keys.length - 1]].splice(index, 1);
    await fetch(`${API}/api/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated) });
    setSettings(updated);
  };

  const Section = ({ title, path, items }) => (
    <div className="setting-section">
      <h4 className="setting-title">{title}</h4>
      <div className="tag-row">
        {(items || []).map((d, i) => (
          <span key={i} className="stag">{d} <button onClick={() => removeItem(path, i)}>x</button></span>
        ))}
      </div>
      <div className="add-row">
        <input className="add-input" placeholder="Add..." value={newVal} onChange={e => setNewVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addItem(path, newVal)} />
        <button className="add-btn" onClick={() => addItem(path, newVal)}>Add</button>
      </div>
    </div>
  );

  return (
    <div className="settings-body">
      <Section title="Priority Sender Domains" path="priority_senders.domains" items={settings.priority_senders?.domains} />
      <Section title="Priority Sender Emails" path="priority_senders.emails" items={settings.priority_senders?.emails} />
      <Section title="Hidden Domains" path="hidden_senders.domains" items={settings.hidden_senders?.domains} />
      <Section title="Hidden Patterns" path="hidden_senders.patterns" items={settings.hidden_senders?.patterns} />
      <Section title="Project Keywords" path="project_keywords" items={settings.project_keywords} />
      <div className="setting-section">
        <h4 className="setting-title">AI Learning</h4>
        <div className="ai-btns-row">
          <button className="ghost-btn" disabled={aiStatus.tones === 'loading'}
            onClick={() => runAiAction('tones', '/api/tone-profiles/refresh')}>
            {aiStatus.tones === 'loading' ? 'Learning...' : aiStatus.tones === 'done' ? '\u2713 Tones Updated' : aiStatus.tones === 'error' ? '\u2717 Failed' : 'Refresh Tones'}
          </button>
          <button className="ghost-btn" disabled={aiStatus.history === 'loading'}
            onClick={() => runAiAction('history', '/api/history/scan')}>
            {aiStatus.history === 'loading' ? 'Scanning...' : aiStatus.history === 'done' ? '\u2713 Scan Complete' : aiStatus.history === 'error' ? '\u2717 Failed' : 'History Scan'}
          </button>
          <button className="ghost-btn" disabled={aiStatus.promote === 'loading'}
            onClick={() => runAiAction('promote', '/api/interactions/promote')}>
            {aiStatus.promote === 'loading' ? 'Promoting...' : aiStatus.promote === 'done' ? '\u2713 Contacts Promoted' : aiStatus.promote === 'error' ? '\u2717 Failed' : 'Promote Contacts'}
          </button>
        </div>
      </div>
      <ProjectMemorySection />
    </div>
  );
}

function ProjectMemorySection() {
  const [memories, setMemories] = useState(null);
  const [scanJobId, setScanJobId] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [editContent, setEditContent] = useState('');
  const scanJob = useJobPoller(scanJobId);

  useEffect(() => {
    fetch(`${API}/api/project-memory`).then(r => r.json()).then(setMemories).catch(() => {});
  }, []);

  const startScan = async () => {
    const res = await fetch(`${API}/api/retroactive-scan`, { method: 'POST' });
    const data = await res.json();
    setScanJobId(data.job_id);
  };

  const saveEdit = async () => {
    if (!editingFile) return;
    await fetch(`${API}/api/project-memory`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editingFile, content: editContent }),
    });
    setEditingFile(null);
    fetch(`${API}/api/project-memory`).then(r => r.json()).then(setMemories);
  };

  return (
    <div className="setting-section">
      <h4 className="setting-title">Project Memory</h4>
      <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
        {memories ? `${memories.files?.length || 0} projects` : 'Loading...'} — Auto-enriched from emails
      </p>
      <div className="ai-btns-row" style={{ marginBottom: '10px' }}>
        <button className="ghost-btn" onClick={startScan} disabled={scanJob?.status === 'processing'}>
          {scanJob?.status === 'processing' ? 'Scanning...' : 'Run Deep Scan (2000 emails)'}
        </button>
      </div>

      {scanJob?.status === 'processing' && (
        <div className="ai-loading"><div className="spinner" /> {scanJob?.detail || 'Scanning...'}</div>
      )}
      {scanJob?.status === 'done' && scanJob.result && (
        <div style={{ fontSize: '11px', color: '#86efac', marginBottom: '8px', padding: '6px 8px', background: 'rgba(34,197,94,0.1)', borderRadius: '4px' }}>
          Scan complete: {scanJob.result.emails_scanned} emails, {scanJob.result.projects_found?.length} projects, {scanJob.result.sent_scanned} sent
        </div>
      )}

      {editingFile ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>{editingFile}.md</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button className="ghost-btn sm" onClick={saveEdit}>Save</button>
              <button className="ghost-btn sm" onClick={() => setEditingFile(null)}>Cancel</button>
            </div>
          </div>
          <textarea style={{ width: '100%', height: '300px', background: '#0d1117', border: '1px solid rgba(255,255,255,0.06)', color: '#e2e8f0', borderRadius: '6px', padding: '8px', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', resize: 'vertical' }}
            value={editContent} onChange={e => setEditContent(e.target.value)} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {memories?.files?.map((f, i) => (
            <button key={i} className="nav-item" style={{ justifyContent: 'space-between' }}
              onClick={() => { setEditingFile(f.name); setEditContent(memories.memories?.[f.name] || ''); }}>
              <span>{f.name}</span>
              <span style={{ fontSize: '10px', color: '#475569' }}>{(f.size / 1024).toFixed(1)}KB</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Chat Bar ─────────────────────────────────────────────────────────────────

function ChatBar() {
  const [query, setQuery] = useState('');
  const [chatJobId, setChatJobId] = useState(null);
  const [open, setOpen] = useState(false);
  const chat = useJobPoller(chatJobId);

  const ask = async () => {
    if (!query.trim()) return;
    setOpen(true);
    const res = await fetch(`${API}/api/chat`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query.trim() }),
    });
    setChatJobId((await res.json()).job_id);
  };

  return (
    <div className="chat-container">
      {open && chat.status && (
        <div className="chat-result">
          <div className="chat-header">
            <span className="chat-q">{query}</span>
            <button className="ghost-btn sm" onClick={() => { setOpen(false); setChatJobId(null); }}>x</button>
          </div>
          {chat.status === 'processing' && <div className="ai-loading"><div className="spinner" /> Searching...</div>}
          {chat.status === 'done' && chat.result && <div className="chat-answer">{chat.result.answer}</div>}
          {chat.status === 'error' && <div className="ai-error">{chat.error}</div>}
        </div>
      )}
      <div className="chat-bar">
        <input className="chat-input" placeholder="Ask about your emails..."
          value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && ask()} />
        <button className="chat-send" onClick={ask}>Ask</button>
      </div>
    </div>
  );
}

export default App;
